import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from '@mui/material';
import { booleanPointInPolygon, point, polygon as tpolygon } from '@turf/turf';
import { makeStyles } from '@material-ui/core/styles';

import Checkbox from '@material-ui/core/Checkbox';
import FormControl from '@material-ui/core/FormControl';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import InputLabel from '@material-ui/core/InputLabel';
import Grid from '@material-ui/core/Grid';
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker, TimePicker } from '@mui/x-date-pickers';
import TextField from '@material-ui/core/TextField';
import moment from 'moment';
import i18n from 'i18next';
import 'moment/locale/fr';
import AutoCompletePlaces from '../../Shared/AutoCompletePlaces';
import { getTripInfo, getTripPrice, isOpenedAtTransportDate } from '../../../Services/DataHelper';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import DeleteIcon from '@material-ui/icons/Delete';
import Paper from '@material-ui/core/Paper';
import Box from '@material-ui/core/Box';
import Typography from '@mui/material/Typography';
import TimeHelper from '../../../Services/TimeHelper';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import { useSelector } from 'react-redux';
import InputAdornment from '@material-ui/core/InputAdornment';
import ClearIcon from '@material-ui/icons/Clear';

moment.locale(i18n?.language);

type Props = {
  lead: Object,
  setLead: Function,
  tripPriceInfo: Object,
  setTripPriceInfo: Function,
  openedAt: Object,
  service: Object,
  organisation: Object,
};

type DateProps = {
  classes: Object,
  AdapterMoment: Object,
  lead: Object,
  checkDepartureDateTimeValidity: Function,
  handleChangeDepartureDate: Function,
  handleChangeDepartureTime: Function,
  isClosedAtDepartureTime: Function,
  isClosedAtReturnTime: Function,
  t: Function,
  handleRoundTrip: Function,
  handleReturnDate: Function,
  handleReturnTime: Function,
  volunteerMaxOnsiteWaitingTime: Function,
};

const useStyles = makeStyles(theme => ({
  dateLayout: {
    flexGrow: 1,
    alignItems: 'center',
  },
  selectLabel: {
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    backgroundColor: '#fff',
  },
  formTitle: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  formControl: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  avatar: {
    marginBottom: 0,
    marginRight: theme.spacing(1),
    width: '25px',
    height: '25px',
    display: 'inline-block',
  },
  duration: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  time: {
    backgroundColor: '#fff',
    borderTopLeftRadius: theme.shape.borderRadius,
    borderTopRightRadius: theme.shape.borderRadius,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: theme.spacing(5),
  },
  subtitle: {
    paddingTop: theme.spacing(1),
    fontStyle: 'italic',
  },
  reminder: {
    fontWeight: 'bold',
    marginTop: theme.spacing(2),
    color: 'green',
    lineHeight: '2',
  },
}));

const DateInputs = ({
  classes,
  AdapterMoment,
  lead,
  checkDepartureDateTimeValidity,
  handleChangeDepartureDate,
  handleChangeDepartureTime,
  isClosedAtDepartureTime,
  isClosedAtReturnTime,
  t,
  handleRoundTrip,
  handleReturnDate,
  handleReturnTime,
  volunteerMaxOnsiteWaitingTime,
}: DateProps) => (
  <Grid container spacing={1}>
    <Grid container className={classes.dateLayout} spacing={3}>
      <Grid item lg={4} md={5} sm={5} xs={12}>
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <DatePicker
            value={lead.departureDate}
            onChange={date => handleChangeDepartureDate(date)}
            label={t('APP.SERVICE.TEXT_FIELD.LABEL.DEPARTURE_DATE')}
            disablePast
            cancelText={t('CANCEL')}
            renderInput={params => (
              <TextField
                {...params}
                variant="outlined"
                required
                margin="normal"
                error={!checkDepartureDateTimeValidity(lead.departureDate, lead.departureTime)}
              />
            )}
          />
        </LocalizationProvider>
      </Grid>
      <Grid item lg={4} md={5} sm={5} xs={12}>
        <LocalizationProvider dateAdapter={AdapterMoment}>
          <TimePicker
            // ampm={false}
            value={lead.departureTime}
            minTime={lead.minDepartureTime}
            onChange={date => handleChangeDepartureTime(date)}
            label={t('START_TIME.LABEL')}
            cancelText={t('CANCEL')}
            minutesStep={5}
            renderInput={params => (
              <TextField
                {...params}
                variant="outlined"
                required
                margin="normal"
                error={
                  isClosedAtDepartureTime(lead) ||
                  !checkDepartureDateTimeValidity(lead.departureDate, lead.departureTime)
                }
                helperText={
                  isClosedAtDepartureTime(lead)
                    ? t('APP.SERVICE.TEXT_FIELD.LABEL.HELPER_TEXT')
                    : !checkDepartureDateTimeValidity(lead.departureDate, lead.departureTime)
                    ? lead.minDepartureDatetime
                      ? 'Date de départ minimum : ' +
                        lead.minDepartureDatetime.format('DD/MM/YYYY HH:mm:ss')
                      : null
                    : null
                }
              />
            )}
          />
        </LocalizationProvider>
      </Grid>
      <Grid item md={4} sm={12} xs={12}>
        {/* return date and time */}
        <FormControl component="div">
          <FormControlLabel
            key={lead.roundTrip}
            label={t('APP.SERVICE.TEXT_FIELD.LABEL.ROUNDTRIP')}
            control={
              <Checkbox
                onClick={event => handleRoundTrip(event.target.checked)}
                checked={lead.roundTrip}
                name="roundTrip"
                color="primary"
              />
            }
          />
        </FormControl>
      </Grid>
    </Grid>
    {lead.roundTrip && (
      <Grid container className={classes.dateLayout} spacing={3}>
        <Grid item lg={4} md={5} sm={5} xs={12}>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DatePicker
              value={lead.returnDate}
              onChange={date => handleReturnDate(date)}
              label={t('APP.SERVICE.TEXT_FIELD.LABEL.RETURN_DATE')}
              disablePast
              disabled={true}
              cancelText={t('CANCEL')}
              minDate={lead.departureDate}
              renderInput={params => (
                <TextField {...params} variant="outlined" required margin="normal" />
              )}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item lg={4} md={5} sm={5} xs={12}>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <TimePicker
              // ampm={false}
              value={lead.returnTime}
              onChange={date => handleReturnTime(date)}
              label={t('BACK_TIME.LABEL')}
              cancelText={t('CANCEL')}
              minutesStep={5}
              renderInput={params => (
                <TextField
                  {...params}
                  variant="outlined"
                  required
                  margin="normal"
                  error={isClosedAtReturnTime(lead) || !volunteerMaxOnsiteWaitingTime().isValid}
                  helperText={
                    isClosedAtReturnTime(lead)
                      ? t('APP.SERVICE.TEXT_FIELD.LABEL.HELPER_TEXT')
                      : !volunteerMaxOnsiteWaitingTime().isValid
                      ? t(
                          'APP.SERVICE.TEXT_FIELD.RETURN_TIME.ERROR.VOLUNTEER_MAX_ONSITE_WAITING_TIME_EXCEEDED',
                          {
                            maxWaitingTimeHhMm: TimeHelper.minutesToTimeString(
                              volunteerMaxOnsiteWaitingTime().minimumReturnTimeInMinutes,
                            ),
                            maxWaitingTimeDurationHhMm:
                              volunteerMaxOnsiteWaitingTime().maxOnsiteWaitingTime,
                          },
                        )
                      : null
                  }
                />
              )}
            />
          </LocalizationProvider>
        </Grid>
      </Grid>
    )}
  </Grid>
);

const QuotationTransport = ({
  lead,
  setLead,
  service,
  tripPriceInfo,
  setTripPriceInfo,
  openedAt,
  organisation,
}: Props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [departureOutOfZone, setDepartureOutOfZone] = useState(false);

  const leagueSettings = useSelector(state => state.leagues.settings || {});
  const travelReasons = leagueSettings.travel_reasons || [];

  const activeTravelReasons = travelReasons.filter(
    reason => reason.active === true || reason.active === 'true',
  );

  const [dateInputs, setDateInputs] = React.useState([]);

  React.useEffect(() => {
    const hasErrors = !areAllDatesValid();
    const waitingTimeResult = lead.roundTrip ? volunteerMaxOnsiteWaitingTime(lead) : {};
    setLead({
      ...lead,
      cloneToDate: dateInputs,
      hasDateValidationErrors: hasErrors,
      chargeTwice: waitingTimeResult.chargeTwice === true,
      chargeTwiceMaxWaitingTime: waitingTimeResult.maxOnsiteWaitingTime || null,
    });
  }, [dateInputs, lead.departureTime, lead.returnTime, lead.departureDate, lead.returnDate]);

  const addDateInput = () => {
    if (dateInputs.length < 9) {
      setDateInputs([
        ...dateInputs,
        {
          departureDate: lead.departureDate,
          departureTime: lead.departureTime,
          returnDate: null,
          returnTime: null,
          minDepartureDatetime: lead.minDepartureDatetime,
        },
      ]);
    }
  };

  const updateDateInput = (index, key, value) => {
    setDateInputs(prevInputs =>
      prevInputs.map((input, i) => {
        if (i !== index) return input;

        const updatedInput = { ...input, [key]: value };

        if (key === 'roundTrip' && value === true) {
          updatedInput.returnDate = input.departureDate;
        }

        if (key === 'roundTrip' && value === false) {
          updatedInput.returnDate = null;
          updatedInput.returnTime = null;
        }

        if (key === 'departureDate') {
          updatedInput.returnDate = value;
        }

        return updatedInput;
      }),
    );
  };

  const removeDateInput = index => {
    setDateInputs(dateInputs.filter((_, i) => i !== index));
  };

  const [departureFrom, setDepartureFrom] = React.useState(lead.departureFrom);
  const [arrivalTo, setArrivalTo] = React.useState(lead.arrivalTo);

  const isClosedAtDepartureTime = dateObject =>
    dateObject.departureTime &&
    !isOpenedAtTransportDate(dateObject.departureDate, dateObject.departureTime, openedAt);

  const isClosedAtReturnTime = dateObject =>
    dateObject.returnTime &&
    !isOpenedAtTransportDate(dateObject.returnDate, dateObject.returnTime, openedAt);

  const volunteerMaxOnsiteWaitingTime = (dateObject = lead) => {
    if (!tripPriceInfo || !tripPriceInfo.duration) {
      return { isValid: true, minimumReturnTimeInMinutes: null };
    }

    if (
      !service ||
      !service.volunteer_max_onsite_waiting_time ||
      service.volunteer_max_onsite_waiting_time <= 0
    ) {
      return { isValid: true, minimumReturnTimeInMinutes: null, maxOnsiteWaitingTime: null };
    }

    const departureTime = dateObject.departureTime;
    const returnTime = dateObject.returnTime;

    if (!departureTime || !returnTime || !dateObject.departureDate || !dateObject.returnDate) {
      return { isValid: true, minimumReturnTimeInMinutes: null, maxOnsiteWaitingTime: null };
    }

    const departure = moment(departureTime);
    const returnMoment = moment(returnTime);

    const departureDate = moment(dateObject.departureDate);
    const returnDate = moment(dateObject.returnDate);

    if (!departureDate.isSame(returnDate, 'day')) {
      return { isValid: true, minimumReturnTimeInMinutes: null, maxOnsiteWaitingTime: null };
    }

    const minimumReturnTime = departure
      .clone()
      .add(service.volunteer_max_onsite_waiting_time + tripPriceInfo.duration + 1, 'minutes');

    const minimumReturnTimeInMinutes =
      minimumReturnTime.hours() * 60 + minimumReturnTime.minutes() - 1;

    returnMoment.year(departure.year()).month(departure.month()).date(departure.date());

    const chargeTwice = returnMoment.isAfter(minimumReturnTime);

    return {
      isValid: true,
      chargeTwice,
      minimumReturnTimeInMinutes: minimumReturnTimeInMinutes,
      maxOnsiteWaitingTime: formatMinutes(service.volunteer_max_onsite_waiting_time),
    };
  };

  const formatMinutes = minutes => {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (remainingMinutes === 0) {
      return `${hours}h`;
    }

    return `${hours}h${remainingMinutes}`;
  };

  const areAllDatesValid = () => {
    const mainDateValid = lead.roundTrip ? volunteerMaxOnsiteWaitingTime(lead).isValid : true;

    const additionalDatesValid = dateInputs.every(dateInput => {
      return dateInput.roundTrip ? volunteerMaxOnsiteWaitingTime(dateInput).isValid : true;
    });

    return mainDateValid && additionalDatesValid;
  };

  const handleChangeDepartureDate = date => {
    setLead({
      ...lead,
      departureDate: date,
      returnDate: date,
      transportDateIsValid: checkDepartureDateTimeValidity(date, lead.departureTime),
    });
  };
  const handleChangeDepartureTime = date => {
    setLead({
      ...lead,
      departureTime: date,
      transportDateIsValid: checkDepartureDateTimeValidity(lead.departureDate, date),
    });
  };

  const checkDepartureDateTimeValidity = (date, time) => {
    if (typeof date === 'string') {
      date = moment(date);
    }
    if (typeof time === 'string') {
      time = moment(time);
    }
    if (date && time) {
      if (!lead.minDepartureDatetime) return true;

      const combinedDate = date.clone().set({
        hour: time.hour(),
        minute: time.minute(),
        second: 0,
        millisecond: 0,
      });

      return combinedDate.isAfter(lead.minDepartureDatetime);
    }

    return false;
  };

  React.useEffect(() => {
    if (lead.departureOutOfZone) return;
    getTripInfo(lead.departureFrom, lead.arrivalTo, lead.roundTrip).then(data => {
      const basePrice = getTripPrice(service, data, lead.needAccompagnement, lead.duration);
      const price = lead.chargeTwice ? basePrice * 2 : basePrice;
      setTripPriceInfo({
        ...data,
        estimatedPrice: price,
        roundTrip: lead.roundTrip,
        needAccompagnement: lead.needAccompagnement,
      });
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead]);

  React.useEffect(() => {
    if (arrivalTo) {
      setLead({
        ...lead,
        arrivalTo: {
          formattedAddress: arrivalTo.formattedAddress,
          latitude: arrivalTo.latitude,
          longitude: arrivalTo.longitude,
          locality: arrivalTo.locality,
          postcode: arrivalTo.postcode,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [arrivalTo]);

  React.useEffect(() => {
    setLead({
      ...lead,
      departureOutOfZone,
      ...(departureFrom && {
        departureFrom: {
          formattedAddress: departureFrom.formattedAddress,
          latitude: departureFrom.latitude,
          longitude: departureFrom.longitude,
          locality: departureFrom.locality,
          postcode: departureFrom.postcode,
        },
      }),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departureFrom, departureOutOfZone]);

  React.useEffect(() => {
    setLead({
      ...lead,
      transportDateIsValid: checkDepartureDateTimeValidity(
        moment(lead.departureDate),
        moment(lead.departureTime),
      ),
    });
  }, []);

  return (
    <>
      <div
        className={classes.reminder}
        dangerouslySetInnerHTML={{ __html: t('APP.QUOTATION.TRANSPORT.ADDRESS.REMINDER') }}
      ></div>
      <FormControl component="fieldset" className={classes.formControl} fullWidth required>
        <InputLabel
          variant="outlined"
          shrink
          className={classes.selectLabel}
          style={{
            zIndex: '1021',
          }}
        >
          {t('APP.SERVICE.TEXT_FIELD.LABEL.DEPARTURE_FROM')}
        </InputLabel>
        <AutoCompletePlaces
          onSelect={value => {
            const rawPolygon = organisation?.intervention_polygon;
            if (rawPolygon?.length > 0) {
              try {
                const ring =
                  rawPolygon[0][0] !== rawPolygon[rawPolygon.length - 1][0] ||
                  rawPolygon[0][1] !== rawPolygon[rawPolygon.length - 1][1]
                    ? [...rawPolygon, rawPolygon[0]]
                    : rawPolygon;
                const pol = tpolygon([ring]);
                if (
                  !booleanPointInPolygon(
                    point([value.coordinates?.lat, value.coordinates?.lng]),
                    pol,
                  )
                ) {
                  setDepartureOutOfZone(true);
                  return;
                }
              } catch (_e) {
                // polygon invalide, on laisse passer
              }
            }
            setDepartureOutOfZone(false);
            setDepartureFrom({
              formattedAddress: value.formatted_address,
              latitude: value.coordinates?.lat,
              longitude: value.coordinates?.lng,
              locality: value.locality,
              postcode: value.postcode,
            });
          }}
          onClear={() => {
            setDepartureOutOfZone(false);
            setDepartureFrom({
              formattedAddress: '',
              latitude: null,
              longitude: null,
              locality: '',
              postcode: '',
            });
          }}
          placeHolder={t('APP.QUOTATION.TRANSPORT.ADDRESS.HELPER')}
          defaultValue={lead.departureFrom?.formattedAddress}
          id="departure"
          style={{ zIndex: 1020 }}
        />
        {departureOutOfZone && (
          <Alert severity="error" style={{ marginTop: 8 }}>
            {t('APP.LEADS.TRANSPORT.DEPARTURE_OUT_OF_ZONE')}
          </Alert>
        )}
      </FormControl>

      <FormControl component="fieldset" className={classes.formControl} fullWidth required>
        <InputLabel
          variant="outlined"
          shrink
          className={classes.selectLabel}
          style={{
            zIndex: '1011',
          }}
        >
          {t('APP.SERVICE.TEXT_FIELD.LABEL.ARRIVAL_TO')}
        </InputLabel>
        <AutoCompletePlaces
          onSelect={value => {
            setArrivalTo({
              formattedAddress: value.formatted_address,
              latitude: value.coordinates?.lat,
              longitude: value.coordinates?.lng,
              locality: value.locality,
              postcode: value.postcode,
            });
          }}
          onClear={() => {
            setArrivalTo({
              formattedAddress: '',
              latitude: null,
              longitude: null,
              locality: '',
              postcode: '',
            });
          }}
          placeHolder={t('APP.QUOTATION.TRANSPORT.ADDRESS.HELPER')}
          defaultValue={lead.arrivalTo?.formattedAddress}
          id="arrival"
        />
      </FormControl>

      {/* departure date and time */}
      <Grid container spacing={2}>
        <Grid item>
          <DateInputs
            t={t}
            lead={lead}
            setLead={setLead}
            classes={classes}
            checkDepartureDateTimeValidity={checkDepartureDateTimeValidity}
            AdapterMoment={AdapterMoment}
            isClosedAtDepartureTime={isClosedAtDepartureTime}
            isClosedAtReturnTime={isClosedAtReturnTime}
            handleChangeDepartureDate={handleChangeDepartureDate}
            handleChangeDepartureTime={handleChangeDepartureTime}
            handleReturnTime={returnTime => setLead({ ...lead, returnTime })}
            handleRoundTrip={roundTrip =>
              setLead({
                ...lead,
                returnDate: roundTrip ? lead.departureDate : null,
                returnTime: roundTrip ? lead.returnTime : null,
                roundTrip,
                chargeTwice: roundTrip ? lead.chargeTwice : false,
                chargeTwiceMaxWaitingTime: roundTrip ? lead.chargeTwiceMaxWaitingTime : null,
              })
            }
            handleReturnDate={returnDate => setLead({ ...lead, returnDate })}
            volunteerMaxOnsiteWaitingTime={volunteerMaxOnsiteWaitingTime}
          />
        </Grid>
      </Grid>
      {dateInputs.length > 0 && (
        <Box pt={3} pb={3}>
          <Typography>Autres dates :</Typography>
        </Box>
      )}
      <Grid container direction={'row'} spacing={3}>
        {dateInputs.map((date, index) => (
          <Grid item>
            <Grid container alignItems={'center'} spacing={2}>
              <Grid item xs={10}>
                <Paper elevation={3} variant={'outlined'}>
                  <Box p={3}>
                    <DateInputs
                      t={t}
                      lead={date}
                      classes={classes}
                      checkDepartureDateTimeValidity={checkDepartureDateTimeValidity}
                      AdapterMoment={AdapterMoment}
                      isClosedAtDepartureTime={isClosedAtDepartureTime}
                      isClosedAtReturnTime={isClosedAtReturnTime}
                      handleChangeDepartureDate={departureDate =>
                        updateDateInput(index, 'departureDate', departureDate)
                      }
                      handleChangeDepartureTime={departureTime =>
                        updateDateInput(index, 'departureTime', departureTime)
                      }
                      handleReturnTime={returnTime =>
                        updateDateInput(index, 'returnTime', returnTime)
                      }
                      handleRoundTrip={roundTrip => updateDateInput(index, 'roundTrip', roundTrip)}
                      handleReturnDate={returnDate =>
                        updateDateInput(index, 'returnDate', returnDate)
                      }
                      volunteerMaxOnsiteWaitingTime={() => volunteerMaxOnsiteWaitingTime(date)}
                    />
                  </Box>
                </Paper>
              </Grid>
              <Grid item xs={2}>
                <IconButton size={'small'} onClick={() => removeDateInput(index)}>
                  <DeleteIcon />
                </IconButton>
              </Grid>
            </Grid>
          </Grid>
        ))}
      </Grid>
      <Box pt={3}>
        <Button onClick={addDateInput} disabled={dateInputs.length >= 9}>
          Ajouter une date
        </Button>
      </Box>

      <FormControl component="fieldset" className={classes.formControl} fullWidth>
        {/* reason */}
        <FormControl variant="outlined" margin="normal" fullWidth>
          {activeTravelReasons.length > 0 ? (
            <>
              <InputLabel id="travel-reason-label">
                {t('APP.SERVICE.TEXT_FIELD.LABEL.REASON')}
              </InputLabel>
              <Select
                required
                labelId="travel-reason-label"
                id="description"
                name="description"
                value={lead.description || ''}
                onChange={event => setLead({ ...lead, description: event.target.value })}
                label={t('APP.SERVICE.TEXT_FIELD.LABEL.REASON')}
                displayEmpty
                endAdornment={
                  lead.description && (
                    <InputAdornment position="end" style={{ marginRight: 32 }}>
                      <IconButton
                        size="small"
                        onClick={e => {
                          e.stopPropagation();
                          setLead({ ...lead, description: '' });
                        }}
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  )
                }
              >
                {activeTravelReasons.map((reason, index) => (
                  <MenuItem key={index} value={reason.name}>
                    {reason.name}
                  </MenuItem>
                ))}
              </Select>
            </>
          ) : (
            <TextField
              required
              id="description"
              name="description"
              value={lead.description}
              type="text"
              onChange={event => setLead({ ...lead, description: event.target.value })}
              label={t('APP.SERVICE.TEXT_FIELD.LABEL.REASON')}
              variant="outlined"
              fullWidth
            />
          )}
        </FormControl>
        <TextField
          required
          id="passagerCount"
          name="passagerCount"
          label={t('APP.SERVICE.TEXT_FIELD.LABEL.PASSAGER_COUNT')}
          value={lead.passagerCount}
          onChange={event => setLead({ ...lead, passagerCount: event.target.value })}
          type="number"
          inputProps={{ min: 1 }}
          InputLabelProps={{
            shrink: true,
          }}
          margin="normal"
          variant="filled"
        />
        <FormControlLabel
          key={lead.needAccompagnement}
          label={t('APP.SERVICE.TEXT_FIELD.LABEL.NEED_ACCOMPAGNEMENT')}
          control={
            <Checkbox
              onClick={event =>
                setLead({
                  ...lead,
                  needAccompagnement: event.target.checked,
                  duration: event.target.checked ? '0.5' : null,
                })
              }
              checked={lead.needAccompagnement}
              name="needAccompagnement"
              color="primary"
            />
          }
        />
        {/* duration */}
        {lead.needAccompagnement && (
          <FormControl className={classes.formControl} margin="normal">
            <RadioGroup
              name="duration"
              value={lead.duration}
              onChange={event => setLead({ ...lead, duration: event.target.value })}
              required
            >
              <FormControlLabel
                value="0.5"
                control={<Radio color="primary" />}
                className={classes.duration}
                label={t('APP.QUOTATION.TRANSPORT.DURATION1.LABEL')}
              />
              <FormControlLabel
                value="1"
                control={<Radio color="primary" />}
                className={classes.duration}
                label={t('APP.QUOTATION.TRANSPORT.DURATION2.LABEL')}
              />
              <FormControlLabel
                value="1.5"
                control={<Radio color="primary" />}
                className={classes.duration}
                label={t('APP.QUOTATION.TRANSPORT.DURATION3.LABEL')}
              />
              <FormControlLabel
                value="2"
                control={<Radio color="primary" />}
                className={classes.duration}
                label={t('APP.QUOTATION.TRANSPORT.DURATION4.LABEL')}
              />
            </RadioGroup>
          </FormControl>
        )}
      </FormControl>
    </>
  );
};

export default QuotationTransport;
